<template>
  <el-dialog
    title="支付宝充值"
    :visible.sync="visible"
    :close-on-click-modal="false"
    :append-to-body="true"
    width="92vw"
    custom-class="activity-payment-dialog"
    @close="closeDialog"
  >
    <div class="payment-layout">
      <div class="payment-code" v-loading="status === 'creating'">
        <div
          v-show="status === 'pending'"
          ref="qrcodeContainer"
          class="payment-qrcode"
          aria-label="支付宝付款二维码"
        ></div>
        <span v-if="status === 'creating'" class="payment-loading">
          正在创建订单
        </span>
        <i
          v-if="
            status === 'error' ||
            status === 'expired' ||
            status === 'unavailable'
          "
          class="el-icon-refresh"
        ></i>
      </div>
      <div class="payment-details">
        <p class="payment-label">充值金额</p>
        <p class="payment-amount">￥{{ nowprice }}</p>
        <p v-if="nowPreferential > 0" class="payment-bonus">
          赠送 {{ nowPreferential }} 元
        </p>
        <p v-if="status === 'pending'" class="payment-instruction">
          请使用支付宝扫一扫，支付码约 3 分钟内有效。
        </p>
        <p v-if="errorMessage" class="payment-error" role="alert">
          {{ errorMessage }}
        </p>
        <el-button
          v-if="status === 'error' || status === 'expired'"
          type="primary"
          size="small"
          @click="getERCodeDatasZFB"
        >
          重新生成支付码
        </el-button>
      </div>
    </div>
    <p class="payment-note">付款成功后将自动刷新余额。</p>
  </el-dialog>
</template>

<script>
import { getPayZFBERCode, queryPayStatusZFB } from "../../../api/pay";
import QRCode from "qrcodejs2";
import { message } from "@/utils/resetMessage";
import { getCampaignPhase } from "@/utils/doubleEleven";

export default {
  props: [
    "dialogVisible",
    "curPreferential",
    "currentPrice",
    "dialogPaying",
    "currentPayType",
    "currentCouponId",
  ],
  data() {
    return {
      visible: false,
      sessionOpen: false,
      status: "idle",
      errorMessage: "",
      nowprice: 0,
      nowPreferential: 0,
      currentOrderId: "",
      requestGeneration: 0,
      pollTimer: null,
      expiryTimer: null,
      expiresAt: 0,
      successEmitted: false,
    };
  },
  watch: {
    dialogVisible: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.closeDialog();
          return;
        }
        if (this.sessionOpen) return;
        this.visible = true;
        this.sessionOpen = true;
        this.nowprice = Number(this.currentPrice);
        this.nowPreferential = Number(this.curPreferential);
        this.getERCodeDatasZFB();
      },
    },
  },
  beforeDestroy() {
    this.closeDialog();
  },
  methods: {
    isCurrentRequest(generation) {
      return (
        this.sessionOpen &&
        this.visible &&
        generation === this.requestGeneration
      );
    },
    clearPaymentWork() {
      // Invalidate pending requests as well as scheduled work, including builds without an order ID.
      this.requestGeneration += 1;
      clearTimeout(this.pollTimer);
      clearTimeout(this.expiryTimer);
      this.pollTimer = null;
      this.expiryTimer = null;
      this.expiresAt = 0;
      this.currentOrderId = "";
      if (this.$refs.qrcodeContainer) {
        this.$refs.qrcodeContainer.innerHTML = "";
      }
    },
    async getERCodeDatasZFB() {
      if (
        !this.sessionOpen ||
        !this.visible ||
        this.status === "creating" ||
        this.status === "pending"
      ) {
        return;
      }
      if (getCampaignPhase() !== "active") {
        this.clearPaymentWork();
        this.status = "unavailable";
        this.errorMessage = "当前不在活动期间，无法生成活动支付码。";
        this.$emit("onPay", false);
        return;
      }
      this.clearPaymentWork();
      const generation = this.requestGeneration;
      this.status = "creating";
      this.errorMessage = "";
      this.successEmitted = false;
      this.$emit("onPay", true);
      try {
        const result = await getPayZFBERCode(
          {
            order_type: "alipay",
            order_price: this.nowprice,
            other_price: this.nowPreferential,
            order_name: Math.random().toString(36).slice(2, 12),
            extension: "pptuku",
          },
          { retry: false }
        );
        if (!this.isCurrentRequest(generation)) return;
        if (!result || !result.orderId || !result.qrUrl) {
          throw new Error("支付信息不完整，请重新生成支付码。");
        }
        this.currentOrderId = result.orderId;
        await this.$nextTick();
        if (!this.isCurrentRequest(generation)) return;
        new QRCode(this.$refs.qrcodeContainer, {
          text: result.qrUrl,
          width: 180,
          height: 180,
          colorDark: "#111827",
          colorLight: "#ffffff",
          correctLevel: QRCode.CorrectLevel.H,
        });
        this.status = "pending";
        this.expiresAt = Date.now() + 3 * 60 * 1000;
        this.expiryTimer = setTimeout(() => {
          if (this.isCurrentRequest(generation)) this.expirePayment();
        }, 3 * 60 * 1000);
        this.schedulePoll(generation, result.orderId);
      } catch (error) {
        if (!this.isCurrentRequest(generation)) return;
        this.clearPaymentWork();
        this.status = "error";
        this.errorMessage = error.message || "暂时无法创建订单，请重试。";
        this.$emit("onPay", false);
      }
    },
    schedulePoll(generation, orderId) {
      if (!this.isCurrentRequest(generation)) return;
      const remaining = this.expiresAt - Date.now();
      if (remaining <= 0) {
        this.expirePayment();
        return;
      }
      this.pollTimer = setTimeout(() => {
        this.pollTimer = null;
        this.pollPayment(generation, orderId);
      }, Math.min(3000, remaining));
    },
    async pollPayment(generation, orderId) {
      if (!this.isCurrentRequest(generation)) return;
      if (Date.now() >= this.expiresAt) {
        this.expirePayment();
        return;
      }
      try {
        const result = await queryPayStatusZFB({ orderId });
        if (!this.isCurrentRequest(generation)) return;
        if (Date.now() >= this.expiresAt || String(result.payStatus) === "3") {
          this.expirePayment();
        } else if (String(result.payStatus) === "1") {
          if (this.successEmitted) return;
          this.successEmitted = true;
          this.closeDialog();
          this.$emit("success", { orderId });
          message.success({
            message: "您已充值成功，谢谢",
            duration: 4000,
            offset: 80,
          });
        } else {
          this.schedulePoll(generation, orderId);
        }
      } catch (error) {
        if (this.isCurrentRequest(generation)) {
          this.schedulePoll(generation, orderId);
        }
      }
    },
    expirePayment() {
      this.clearPaymentWork();
      this.status = "expired";
      this.errorMessage = "支付码已超时，请确认付款结果后重新生成。";
      this.$emit("onPay", false);
    },
    closeDialog() {
      const notifyParent = this.sessionOpen;
      this.sessionOpen = false;
      this.visible = false;
      this.clearPaymentWork();
      this.status = "idle";
      this.errorMessage = "";
      this.$emit("onPay", false);
      if (notifyParent) this.$emit("closeDialog");
    },
  },
};
</script>

<style lang="scss">
.activity-payment-dialog {
  max-width: 560px;
  border-radius: 16px;

  .el-dialog__body {
    padding: 24px;
  }

  .payment-layout {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .payment-code {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 180px;
    width: 180px;
    height: 180px;
    padding: 8px;
    box-sizing: content-box;
    border: 1px solid #e5e7eb;
    border-radius: 12px;

    > i {
      color: #9ca3af;
      font-size: 40px;
    }
  }

  .payment-loading {
    color: #6b7280;
    font-size: 13px;
  }

  .payment-qrcode {
    width: 180px;
    height: 180px;
  }

  .payment-details {
    min-width: 0;
  }

  .payment-label,
  .payment-instruction {
    margin: 0 0 10px;
    color: #6b7280;
    line-height: 1.7;
  }

  .payment-amount {
    margin: 0 0 8px;
    color: #111827;
    font-size: 32px;
    font-weight: 700;
  }

  .payment-bonus {
    margin: 0 0 12px;
    color: #c2410c;
    font-weight: 600;
  }

  .payment-error {
    color: #b91c1c;
    line-height: 1.7;
  }

  .payment-note {
    margin: 24px 0 0;
    padding-top: 16px;
    border-top: 1px solid #f3f4f6;
    color: #6b7280;
    font-size: 12px;
  }
}

@media (max-width: 540px) {
  .activity-payment-dialog .payment-layout {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
}
</style>
